import { useEffect, useState } from "react";
import api, { unwrap } from "@/lib/api";
import { EP } from "@/lib/endpoints";

/**
 * useAuthor — 1:1 port of iOS `mobile/src/lib/xano/authors.ts` `useAuthor`.
 *
 * Some Xano endpoints emit only a bare `user_id` on a nested item (e.g.
 * different-view comments, replies-only-with-user_id).  This hook fetches
 * `GET /users/{user_id}` once per session and caches the result in a
 * module-level Map so every DvRow / Reply that references the same user
 * hydrates from a single request.
 *
 * `seed` is an optional partial author object already known from the
 * payload — used to avoid the fetch entirely if `display_name` /
 * `username` / `profile_photo_url` are all already present.
 */

const cache = new Map(); // user_id -> author record (or `null` on miss)
const inflight = new Map(); // user_id -> Promise<author | null>

function hasEnoughInline(seed) {
  if (!seed || typeof seed !== "object") return false;
  const hasName = seed.display_name || seed.name;
  const hasHandle = seed.username;
  const hasAvatar =
    seed.profile_photo_url ||
    seed.profile_photo ||
    seed.avatar_url ||
    seed.avatar ||
    seed.photo_url ||
    seed.profile_photo_media_id;
  // We only skip the fetch when the seed carries display_name + username
  // AND at least one avatar field. Otherwise we let the fetch run — the
  // profile endpoint is where the avatar lives.
  return !!(hasName && hasHandle && hasAvatar);
}

async function fetchAuthor(uid) {
  const key = String(uid);
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    try {
      // Step 1: identity record — `GET /api:brontoy-users-prod/users/{id}`.
      // This carries username / display_name but NOT the profile photo.
      const res = await api.get(EP.userById(uid));
      let a = unwrap(res.data);
      a = a?.user || a?.profile || a;
      if (Array.isArray(a)) a = a[0];
      if (!a || typeof a !== "object") {
        cache.set(key, null);
        return null;
      }

      // Step 2: enrich the avatar via
      // `GET /api:brontoy-profiles/by_username?username=…` when the
      // identity record didn't inline any avatar field. This is the
      // exact hydration chain iOS spec (§15_OTHER_USER.md line 1435)
      // requires so PersonRow / DifferentViewItem show real photos.
      const hasAvatar =
        a.profile_photo_url ||
        a.profile_photo ||
        a.avatar_url ||
        a.avatar ||
        a.photo_url ||
        a.profile_photo_media_id;
      if (a.username && !hasAvatar) {
        try {
          const pres = await api.get(EP.profileByUsername(a.username));
          let prof = unwrap(pres.data);
          prof = prof?.profile || prof?.user || prof;
          if (Array.isArray(prof)) prof = prof[0];
          if (prof && typeof prof === "object") {
            // Profile record wins for avatar / bio / counts; user
            // record's username & display_name are preserved when the
            // profile doesn't override them.
            a = { ...a, ...prof };
          }
        } catch {
          // Photo-less accounts sometimes make profileByUsername throw
          // ("profile media missing" — see UserProfile.jsx fix). Not
          // fatal: UserAvatar falls back to initials gradient.
        }
      }

      cache.set(key, a);
      return a;
    } catch {
      cache.set(key, null);
      return null;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

export function useAuthor(userId, seed) {
  const uid = userId != null ? String(userId) : null;
  const inlineReady = hasEnoughInline(seed);
  const cached = uid ? cache.get(uid) : undefined;
  // Author merge — NO fake "user42" / "User 42" placeholders. Callers
  // read `_hydrating` to decide whether to render a skeleton for the
  // in-flight moment before the network fetch resolves. Once resolved,
  // real fields overwrite the empty seed.
  const clean = (o) => {
    if (!o || typeof o !== "object") return {};
    const out = {};
    for (const [k, v] of Object.entries(o)) {
      if (v !== undefined && v !== null && v !== "") out[k] = v;
    }
    return out;
  };
  const merge = (base, ready) => ({
    // Preserve the id fields even before hydration so callers keying
    // on user_id (`isSelf` checks, `data-testid`, follow buttons)
    // continue to work.
    id: userId,
    user_id: userId,
    ...clean(seed),
    ...clean(base),
    // Signals "I'm still waiting for /users/{id} + /profiles/by_username".
    // When true, display_name / username / avatar may still be empty.
    _hydrating: !ready,
  });

  const initialReady = inlineReady || cached !== undefined;
  const [author, setAuthor] = useState(() => merge(cached, initialReady));

  useEffect(() => {
    if (!uid) return;
    if (inlineReady) {
      setAuthor(merge(null, true));
      return;
    }
    const hit = cache.get(uid);
    if (hit !== undefined) {
      setAuthor(merge(hit, true));
      return;
    }
    let cancelled = false;
    // While fetching, keep _hydrating: true so rows show skeletons
    // rather than an empty "?" avatar + missing name.
    setAuthor((prev) => (prev?._hydrating ? prev : merge(null, false)));
    fetchAuthor(uid).then((a) => {
      if (!cancelled) setAuthor(merge(a, true));
    });
    return () => {
      cancelled = true;
    };
  }, [uid, inlineReady]);

  return author;
}

// Prime the cache from a payload that already carries the author record
// (e.g. reply.author) so subsequent grid tiles / rows for the same user
// don't refetch.
export function primeAuthor(a) {
  if (!a || typeof a !== "object") return;
  const uid = a.user_id ?? a.id;
  if (uid == null) return;
  cache.set(String(uid), a);
}

/**
 * extractPerson — normalize a follow-edge / follower / following record
 * into a flat person object.
 *
 * Handles the actual Xano shape (verified 2026-02 against
 * `/api:brontoy-profiles/by_username_followers` and
 * `/api:brontoy-profiles/by_username_following`):
 *
 * ```json
 * // followers item
 * { id, follower_id, following_id, created_at,
 *   follower_username, follower_display_name,
 *   follower_photo_id, follower_photo_url }
 *
 * // following item
 * { id, follower_id, following_id, created_at,
 *   following_username, following_display_name,
 *   following_photo_id, following_photo_url }
 * ```
 *
 * Also tolerates the legacy nested shapes documented in iOS spec
 * `15_OTHER_USER.md` (person nested under `follower` / `_follower` /
 * `user` / `_user` / `profile` / `_profile` / `author` /
 * `_author`), and bare id-only edges.
 *
 * `kind` is `"follower"` (for the followers list) or `"following"`
 * (for the following list) — controls which prefix + nested key we
 * pick first.
 */
export function extractPerson(item, kind = "follower") {
  if (!item || typeof item !== "object") return null;

  // 1. Flat prefixed shape — the current Xano contract for
  //    by_username_followers / by_username_following.
  //    Followers list rows have `follower_*` fields describing the
  //    person following you; following list rows have `following_*`
  //    fields describing the person you follow.
  const prefix = kind === "following" ? "following_" : "follower_";
  const flatUsername = item[`${prefix}username`];
  const flatId = item[`${prefix}id`];
  if (flatUsername || flatId != null) {
    const person = {
      user_id: flatId ?? item.user_id ?? item.id,
      id: flatId ?? item.user_id ?? item.id,
      username: flatUsername,
      display_name: item[`${prefix}display_name`],
      // Both inline URL and media_id are supported by UserAvatar via
      // useAvatarUrl — pass whichever we have.
      profile_photo_url: item[`${prefix}photo_url`] || undefined,
      profile_photo_media_id:
        item[`${prefix}photo_id`] && item[`${prefix}photo_id`] !== 0
          ? item[`${prefix}photo_id`]
          : undefined,
      is_verified: item[`${prefix}is_verified`] ?? item.is_verified,
      bio: item[`${prefix}bio`] ?? item.bio,
      // Follow flags might live on the edge or be inferred: if this is
      // the "following" list and we're viewing our own, every row here
      // means we already follow that person.
      viewer_is_following:
        item.viewer_is_following ??
        item.is_following ??
        (kind === "following" ? true : undefined),
      is_following: item.is_following,
      viewer_follow_pending:
        item.viewer_follow_pending ?? item.follow_pending,
      follow_pending: item.follow_pending,
    };
    // Drop undefined so seed short-circuit checks work correctly.
    const cleaned = {};
    for (const [k, v] of Object.entries(person)) {
      if (v !== undefined && v !== null && v !== "") cleaned[k] = v;
    }
    if (cleaned.user_id != null || cleaned.username) return cleaned;
  }

  // 2. Legacy fully-nested shape (kept as a safety net for older Xano
  //    endpoints that emit `_follower: {...}` etc).
  const preferredKeys =
    kind === "following"
      ? [
          "following",
          "_following",
          "followee",
          "_followee",
          "target",
          "_target",
        ]
      : ["follower", "_follower", "source", "_source"];
  const genericKeys = [
    "user",
    "_user",
    "profile",
    "_profile",
    "author",
    "_author",
    "person",
    "_person",
  ];
  if (item.username) return item;
  for (const k of [...preferredKeys, ...genericKeys]) {
    const v = item[k];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      if (v.username || v.user_id != null || v.id != null) {
        return {
          ...v,
          viewer_is_following: v.viewer_is_following ?? item.viewer_is_following,
          is_following: v.is_following ?? item.is_following,
          viewer_follow_pending:
            v.viewer_follow_pending ?? item.viewer_follow_pending,
          follow_pending: v.follow_pending ?? item.follow_pending,
        };
      }
    }
  }

  // 3. Bare id-only edge — surface `user_id` so PersonRow hydrates via
  //    useAuthor. NB: `item.id` here is the follow-edge id, NOT the
  //    person's id, so we ONLY fall back to it when no explicit
  //    person-id field exists.
  const uid =
    item.user_id ??
    item.follower_user_id ??
    item.following_user_id ??
    item.target_user_id ??
    item.source_user_id;
  if (uid != null) return { user_id: uid, id: uid };
  return null;
}
